import type { RSVP } from '@/types';
import { getRsvpAttendeeQuantity } from '@/lib/event-utils';

/**
 * Multi-tenant bookings hold several tickets on one RSVP, but only the booker has a name. The table
 * planner seats each extra ticket as a "Guest of …" placeholder so the whole party can be allocated.
 * Placeholders are never written to Firestore; only their ids appear in table plan seating.
 */
const PLACEHOLDER_ID_SEPARATOR = '__guestOf__';

export function buildGuestOfPlaceholderId(hostRsvpId: string, index: number): string {
  return `${hostRsvpId}${PLACEHOLDER_ID_SEPARATOR}${index}`;
}

type PlaceholderHost = Pick<RSVP, 'name' | 'firstName' | 'lastName' | 'rank' | 'rankShortName'>;

/** "W.Bro. John Smith" — the booker's title (rank prefix) plus first and last name. */
function formatHostLabel(host: PlaceholderHost): string {
  const title = host.rank?.trim() || host.rankShortName?.trim() || '';
  const fullName = [host.firstName, host.lastName].filter(Boolean).join(' ').trim() || host.name?.trim() || '';
  return [title, fullName].filter(Boolean).join(' ');
}

/** "Guest of W.Bro. John Smith". */
export function formatGuestOfPlaceholderName(host: PlaceholderHost): string {
  const hostLabel = formatHostLabel(host);
  return hostLabel ? `Guest of ${hostLabel}` : 'Guest';
}

/**
 * Appends one placeholder per ticket beyond the first on each multi-ticket booking. The booker keeps
 * their own entry (ticket 1); placeholders are linked back to them via `invitedBy` so relationship
 * colours and auto-populate keep the party together.
 */
export function expandMultiTenantGuestPlaceholders(rsvps: RSVP[]): RSVP[] {
  const expanded: RSVP[] = [];
  for (const host of rsvps) {
    expanded.push(host);
    const extraTickets = getRsvpAttendeeQuantity(host) - 1;
    if (extraTickets <= 0) continue;
    const hostLabel = formatHostLabel(host);
    const name = hostLabel ? `Guest of ${hostLabel}` : 'Guest';
    for (let i = 1; i <= extraTickets; i++) {
      expanded.push({
        id: buildGuestOfPlaceholderId(host.id, i),
        eventId: host.eventId,
        userId: '',
        name,
        // Split so first + last name renders the full label too (place card name tokens, PDF exports).
        firstName: 'Guest',
        lastName: hostLabel ? `of ${hostLabel}` : '',
        rank: '',
        unit: '',
        email: '',
        responses: {},
        status: host.status,
        // Inherit the booker's attendance so place cards drop the whole party with them.
        apologies: host.apologies,
        invoiceId: host.invoiceId,
        paid: host.paid,
        invitedBy: host.id,
        tag: host.tag ?? null,
        checkedIn: false,
        plusOneAllowed: false,
        createdAt: host.createdAt,
        updatedAt: host.updatedAt,
      });
    }
  }
  return expanded;
}
